/**
 * Project Template detail/edit page for /admin/project-templates/:id route.
 * Allows viewing and editing template details including document templates.
 * Admin-only access.
 */

import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { adminRoutes, useWorkspaceUrl } from "../lib/routes";
import { PageHeader } from "../components/layout/PageHeader";
import {
  getProjectTemplate,
  updateProjectTemplate,
  deleteProjectTemplate,
  setDefaultProjectTemplate,
  duplicateProjectTemplate,
  getProjectTemplateUsage,
  getShapeRecipes,
  adminGetAllWorkspaces,
  addWorkspaceToTemplate,
  removeWorkspaceFromTemplate,
  type ProjectTemplate,
  type ProjectTemplateUsage,
  type ShapeRecipe,
  type AdminWorkspace,
  type Workspace,
} from "../services/naraDoor";
import {
  Layers,
  ArrowLeft,
  Pencil,
  Save,
  X,
  RefreshCw,
  AlertCircle,
  Star,
  Copy,
  Trash2,
  Plus,
  FileText,
  MoreVertical,
  Check,
  Building2,
} from "lucide-react";
import { useProfile } from "../contexts/ProfileContext";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { KiiList, type KiiListColumn, type FilterConfig, type FilterValues } from "@/components/kii";
import { TableRow, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ExtractionRecipePicker } from "../components/ExtractionRecipePicker";
import { canGenerateDocument } from "../lib/recipeModes";

export function ProjectTemplateDetail() {
  const { t } = useTranslation();
  const { id: templateId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin, isLoading: isProfileLoading } = useProfile();
  const urls = useWorkspaceUrl();

  const { formatDate } = useDateFormatter();

  // T-365: saving state of the extraction recipe picker (above the admin redirect: hooks keep their order).
  const [isSavingExtraction, setIsSavingExtraction] = useState(false);
  const [extractionSaveFailed, setExtractionSaveFailed] = useState(false);

  // Redirect non-admins to dashboard
  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={urls?.dashboard || "/dashboard"} replace />;
  }

  const { data: templateDetails, isLoading, error: templateError } = useQuery({
    queryKey: ["adminProjectTemplate", templateId],
    queryFn: () => getProjectTemplate(templateId!),
    enabled: !!templateId,
  });
  const error = templateError?.message ?? null;

  // Usage is optional - don't fail if endpoint doesn't exist
  const { data: usage } = useQuery({
    queryKey: ["projectTemplateUsage", templateId],
    queryFn: async (): Promise<ProjectTemplateUsage> => {
      try {
        return await getProjectTemplateUsage(templateId!);
      } catch (usageErr) {
        console.warn("Failed to fetch template usage:", usageErr);
        return { workspaceCount: 0, projectCount: 0, canDelete: true, isDefault: false };
      }
    },
    enabled: !!templateId,
  });

  // Shape recipes to display names (same key as the NaraShape list)
  const { data: shapeRecipesData, isLoading: isLoadingShapeRecipes } = useQuery({
    queryKey: ["shapeRecipes"],
    queryFn: () => getShapeRecipes(),
  });
  const availableShapeRecipes: ShapeRecipe[] = shapeRecipesData?.recipes || [];
  // An extraction recipe generates no document: it is set through its own slot, never assigned.
  const assignableShapeRecipes = availableShapeRecipes.filter(canGenerateDocument);

  // T-365: the third recipe slot. The picker sets it; "" clears it.
  const handleExtractionRecipeChange = async (recipeId: string) => {
    setIsSavingExtraction(true);
    setExtractionSaveFailed(false);
    try {
      await updateProjectTemplate(templateId!, { defaultExtractionRecipeId: recipeId });
    } catch (err) {
      console.error("Failed to set default extraction recipe", { templateId, error: err instanceof Error ? err.message : String(err) });
      setExtractionSaveFailed(true);
    } finally {
      setIsSavingExtraction(false);
    }
  };

  // All workspaces for name resolution (admin page)
  const { data: allWorkspacesData } = useQuery({
    queryKey: ["adminTemplateWorkspaces"],
    queryFn: () => adminGetAllWorkspaces(0, 100),
  });
  const allWorkspaces: AdminWorkspace[] = allWorkspacesData?.workspaces || [];

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Edit form state
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editScope, setEditScope] = useState<"global" | "workspace">("global");
  const [editWorkspaceIds, setEditWorkspaceIds] = useState<string[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Actions menu state
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // Document recipes state
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [draftRecipeIds, setDraftRecipeIds] = useState<string[]>([]);

  // Delete confirmation state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Action states
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);

  // Workspace assignment state
  const [workspaceSearch, setWorkspaceSearch] = useState("");
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);
  const [workspaceFilterValues, setWorkspaceFilterValues] = useState<FilterValues>({ status: null });

  const handleSave = async () => {
    const trimmedName = editName.trim();
    const trimmedDescription = editDescription.trim();

    if (!trimmedName) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      // Workspace picks are a draft until Save; a Global template ignores them.
      // Assign before the scope change so a template never goes Workspace with no workspaces.
      if (editScope === "workspace") {
        const saved = templateDetails!.workspace_ids || [];
        await Promise.all([
          ...editWorkspaceIds.filter(id => !saved.includes(id)).map(id => addWorkspaceToTemplate(templateId!, id)),
          ...saved.filter(id => !editWorkspaceIds.includes(id)).map(id => removeWorkspaceFromTemplate(templateId!, id)),
        ]);
      }

      const scopeChanged = editScope !== templateDetails!.scope;
      await updateProjectTemplate(templateId!, {
        name: trimmedName,
        description: trimmedDescription,
        ...(scopeChanged ? { scope: editScope } : {}),
      });

      setIsEditing(false);
    } catch (err: any) {
      console.error("Failed to update template:", err);
      setSaveError(t("projectTemplates.detail.saveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  // The draft starts from the current server data each time editing begins.
  const handleStartEdit = () => {
    setEditName(templateDetails?.name || "");
    setEditDescription(templateDetails?.description || "");
    setEditScope(templateDetails?.scope || "global");
    setEditWorkspaceIds(templateDetails?.workspace_ids || []);
    setSaveError(null);
    setIsEditing(true);
  };

  const handleCancel = () => setIsEditing(false);

  const handleSetDefault = async () => {
    setIsSettingDefault(true);
    setShowActionsMenu(false);

    try {
      await setDefaultProjectTemplate(templateId!);
    } catch (err: any) {
      console.error("Failed to set as default:", err);
    } finally {
      setIsSettingDefault(false);
    }
  };

  const handleDuplicate = async () => {
    setIsDuplicating(true);
    setShowActionsMenu(false);

    try {
      const newTemplate = await duplicateProjectTemplate(templateId!);
      navigate(adminRoutes.projectTemplate(newTemplate.id));
    } catch (err: any) {
      console.error("Failed to duplicate template:", err);
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);

    try {
      await deleteProjectTemplate(templateId!);
      navigate(adminRoutes.projectTemplates);
    } catch (err: any) {
      console.error("Failed to delete template:", err);
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleOpenAssignDialog = () => {
    setDraftRecipeIds(templateDetails?.document_recipe_ids || []);
    setShowAssignDialog(true);
  };

  const handleToggleDocumentRecipe = (shapeTemplateId: string) => {
    setDraftRecipeIds((prev) =>
      prev.includes(shapeTemplateId)
        ? prev.filter((id) => id !== shapeTemplateId)
        : [...prev, shapeTemplateId]
    );
  };

  const handleSaveDocumentRecipes = async () => {
    setIsSaving(true);

    try {
      await updateProjectTemplate(templateId!, {
        documentRecipeIds: draftRecipeIds,
      });
      setShowAssignDialog(false);
    } catch (err: any) {
      console.error("Failed to update document recipes:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveDocumentRecipe = async (shapeTemplateId: string) => {
    const newIds = (templateDetails?.document_recipe_ids || []).filter((id) => id !== shapeTemplateId);

    // Clear defaults if the removed recipe was a default
    const clearTranscriptionDefault = templateDetails?.default_transcription_recipe_id === shapeTemplateId;
    const clearProjectDefault = templateDetails?.default_project_recipe_id === shapeTemplateId;
    const clearExtractionDefault = templateDetails?.default_extraction_recipe_id === shapeTemplateId;

    try {
      const updateData: Parameters<typeof updateProjectTemplate>[1] = {
        documentRecipeIds: newIds,
      };
      if (clearTranscriptionDefault) {
        updateData.defaultTranscriptionRecipeId = "";
      }
      if (clearProjectDefault) {
        updateData.defaultProjectRecipeId = "";
      }
      if (clearExtractionDefault) {
        updateData.defaultExtractionRecipeId = "";
      }
      await updateProjectTemplate(templateId!, updateData);
    } catch (err: any) {
      console.error("Failed to remove document recipe:", err);
    }
  };

  const toggleDraftWorkspace = (workspaceId: string) =>
    setEditWorkspaceIds(ids => ids.includes(workspaceId) ? ids.filter(id => id !== workspaceId) : [...ids, workspaceId]);

  const getWorkspaceName = (workspaceId: string): string => {
    const ws = allWorkspaces.find(w => w.id === workspaceId);
    return ws?.name || workspaceId;
  };

  const workspaceStatusFilters: FilterConfig[] = [
    {
      id: "status",
      label: "Status",
      placeholder: "All",
      colorVariant: "primary",
      multiple: false,
      options: [
        { value: "assigned", label: "Assigned" },
        { value: "unassigned", label: "Not assigned" },
      ],
    },
  ];

  const workspaceColumns: KiiListColumn[] = [
    { key: "name", label: "Workspace" },
    { key: "status", label: "Status" },
    { key: "actions", label: "" },
  ];

  const assignedWorkspaceIds = isEditing ? editWorkspaceIds : templateDetails?.workspace_ids || [];

  const workspacesWithStatus = useMemo(() => {
    const assignedIds = new Set(assignedWorkspaceIds);
    // A deleted workspace stays listed only while it is assigned, so it can still be removed.
    let list = allWorkspaces.filter(ws => !ws.deleted_at || assignedIds.has(ws.id)).map(ws => ({
      ...ws,
      isAssigned: assignedIds.has(ws.id),
    }));
    // Apply status filter
    const statusFilter = workspaceFilterValues.status as string | null;
    if (statusFilter === "assigned") {
      list = list.filter(ws => ws.isAssigned);
    } else if (statusFilter === "unassigned") {
      list = list.filter(ws => !ws.isAssigned);
    }
    if (workspaceSearch) {
      const q = workspaceSearch.toLowerCase();
      list = list.filter(ws =>
        ws.name?.toLowerCase().includes(q) ||
        ws.handle?.toLowerCase().includes(q)
      );
    }
    // Assigned workspaces first
    list.sort((a, b) => (a.isAssigned === b.isAssigned ? 0 : a.isAssigned ? -1 : 1));
    return list;
  }, [allWorkspaces, assignedWorkspaceIds, workspaceSearch, workspaceFilterValues]);

  const handleMakeExclusive = async (workspaceId: string) => {
    setIsSaving(true);
    setWorkspaceError(null);
    try {
      await updateProjectTemplate(templateId!, {
        workspaceId: workspaceId,
      });
    } catch (err: any) {
      console.error("Failed to make template exclusive:", err);
      setWorkspaceError(err.message || "Failed to make template exclusive.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleReleaseOwnership = async () => {
    setIsSaving(true);
    setWorkspaceError(null);
    try {
      await updateProjectTemplate(templateId!, {
        workspaceId: "", // Empty string clears ownership
      });
    } catch (err: any) {
      console.error("Failed to release ownership:", err);
      setWorkspaceError(err.message || "Failed to release ownership.");
    } finally {
      setIsSaving(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-48 bg-muted rounded animate-pulse" />
        <div className="h-6 w-96 bg-muted rounded animate-pulse" />
        <div className="h-64 bg-muted rounded animate-pulse" />
      </div>
    );
  }

  // Error state
  if (error || !templateDetails) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 text-center">
        <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-lg font-medium text-foreground mb-2">
          {t("projectTemplates.detail.notFound")}
        </h2>
        <p className="text-muted-foreground mb-4">
          {error || t("projectTemplates.detail.notFoundDescription")}
        </p>
        <Link
          to={adminRoutes.projectTemplates}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("projectTemplates.detail.backToTemplates")}
        </Link>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={templateDetails.name || t("projectTemplates.detail.template")}
        onTitleSave={(newName) => {
          updateProjectTemplate(templateId!, { name: newName }).catch((err) =>
            console.error("Failed to rename template:", err)
          );
        }}
        isAdmin
        breadcrumbs={[
          { label: t("projectTemplates.breadcrumbs.admin") },
          { label: t("projectTemplates.breadcrumbs.projectTemplates"), href: adminRoutes.projectTemplates },
          { label: templateDetails.name || templateId || t("projectTemplates.detail.template") },
        ]}
        actions={
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="inline-flex items-center gap-2 px-3 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
                >
                  <X className="h-4 w-4" />
                  {t("projectTemplates.detail.cancel")}
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving || !editName.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isSaving ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      {t("projectTemplates.detail.saving")}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      {t("projectTemplates.detail.saveChanges")}
                    </>
                  )}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate(adminRoutes.projectTemplates)}
                  className="inline-flex items-center gap-2 px-3 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t("projectTemplates.detail.back")}
                </button>
                <button
                  onClick={handleStartEdit}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Pencil className="h-4 w-4" />
                  {t("projectTemplates.detail.edit")}
                </button>
                {/* Actions dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowActionsMenu(!showActionsMenu)}
                    className="inline-flex items-center gap-2 px-3 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                  {showActionsMenu && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowActionsMenu(false)}
                      />
                      <div className="absolute right-0 mt-2 w-48 bg-card rounded-lg shadow-lg border border-border z-20">
                        {!usage?.isDefault && (
                          <button
                            onClick={handleSetDefault}
                            disabled={isSettingDefault}
                            className="w-full flex items-center gap-2 px-4 py-2 text-left text-foreground hover:bg-accent transition-colors disabled:opacity-50"
                          >
                            <Star className="h-4 w-4" />
                            {isSettingDefault ? t("projectTemplates.detail.setting") : t("projectTemplates.detail.setAsDefault")}
                          </button>
                        )}
                        <button
                          onClick={handleDuplicate}
                          disabled={isDuplicating}
                          className="w-full flex items-center gap-2 px-4 py-2 text-left text-foreground hover:bg-accent transition-colors disabled:opacity-50"
                        >
                          <Copy className="h-4 w-4" />
                          {isDuplicating ? t("projectTemplates.detail.duplicating") : t("projectTemplates.detail.duplicate")}
                        </button>
                        <button
                          onClick={() => {
                            setShowActionsMenu(false);
                            setShowDeleteDialog(true);
                          }}
                          disabled={!usage?.canDelete}
                          className="w-full flex items-center gap-2 px-4 py-2 text-left text-red-600 hover:bg-red-50 dark:hover:bg-red-950 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="h-4 w-4" />
                          {t("projectTemplates.detail.delete")}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        }
      />

      <div className="space-y-6">
        {/* Project template */}
        <div className="bg-card rounded-lg border border-border">
          <div className="px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-medium text-foreground">{t("projectTemplates.detail.projectTemplate")}</h3>
              {usage?.isDefault && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50 rounded-full">
                  <Star className="h-3 w-3" />
                  {t("projectTemplates.scope.default")}
                </span>
              )}
            </div>
          </div>
          <div className="p-4 space-y-4">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("projectTemplates.detail.id")}</label>
                <div className="flex items-center h-10 px-3 border border-border rounded-lg bg-muted text-muted-foreground">
                  <code className="font-mono text-sm">{templateId}</code>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{t("projectTemplates.detail.idHint")}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("projectTemplates.detail.name")}</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    maxLength={255}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <div className="flex items-center h-10 px-3 border border-border rounded-lg bg-muted">
                    {templateDetails.name || "—"}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1">{t("projectTemplates.detail.nameHint")}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Scope</label>
                {isEditing ? (
                  <select
                    value={editScope}
                    onChange={(e) => setEditScope(e.target.value as "global" | "workspace")}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-background"
                  >
                    <option value="global">Global — Available to all workspaces</option>
                    <option value="workspace">Workspace — Restricted to specific workspaces</option>
                  </select>
                ) : (
                  <div className="flex items-center h-10 px-3 border border-border rounded-lg bg-muted">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full ${
                      templateDetails.scope === "global"
                        ? "text-green-700 bg-green-50 dark:text-green-300 dark:bg-green-950"
                        : "text-blue-700 bg-blue-50 dark:text-blue-300 dark:bg-blue-950"
                    }`}>
                      {templateDetails.scope === "global" ? "Global" : "Workspace"}
                    </span>
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Global templates are available to all workspaces. Workspace templates are restricted.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t("projectTemplates.detail.description")}</label>
              {isEditing ? (
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder={t("projectTemplates.detail.descriptionPlaceholder")}
                  className="w-full h-20 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              ) : (
                <div className="px-3 py-2 border border-border rounded-lg bg-muted min-h-[80px]">
                  <p className="text-sm text-foreground whitespace-pre-wrap">
                    {templateDetails.description || t("projectTemplates.detail.noDescription")}
                  </p>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {t("projectTemplates.detail.descriptionHint")}
              </p>
            </div>

            {saveError && (
              <div role="alert" className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{saveError}</span>
              </div>
            )}

            {/* Usage stats */}
            {usage && (
              <div className="grid gap-4 sm:grid-cols-2 pt-2 border-t border-border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{t("projectTemplates.detail.workspacesUsing")}</span>
                  <span className="font-medium text-foreground">{usage.workspaceCount}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{t("projectTemplates.detail.projectsUsing")}</span>
                  <span className="font-medium text-foreground">{usage.projectCount}</span>
                </div>
              </div>
            )}

            {templateDetails.created_at && (
              <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                {t("projectTemplates.detail.created", { date: formatDate(templateDetails.created_at, "long-datetime") })}
              </div>
            )}
          </div>
        </div>

        {/* Workspace Access — follows the scope being edited, so picking "Workspace" shows the list at once */}
        {(isEditing ? editScope : templateDetails.scope) === "global" ? (
          <div className="bg-card rounded-lg border border-border">
            <div className="px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <h3 className="text-lg font-medium text-foreground">Workspace Access</h3>
              </div>
              <p className="text-sm text-muted-foreground">This template is available to all workspaces.</p>
            </div>
            <div className="p-4">
              <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
                <Building2 className="h-5 w-5 text-green-600" />
                <div>
                  <span className="text-sm font-medium text-foreground">Available to all workspaces</span>
                  <p className="text-xs text-muted-foreground">
                    Change the scope to "Workspace" to restrict access to specific workspaces.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : templateDetails.workspace_id ? (
          <div className="bg-card rounded-lg border border-border">
            <div className="px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <h3 className="text-lg font-medium text-foreground">Workspace Access</h3>
              </div>
              <p className="text-sm text-muted-foreground">This template is owned by a specific workspace.</p>
            </div>
            <div className="p-4 space-y-3">
              {workspaceError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span>{workspaceError}</span>
                  <button
                    onClick={() => setWorkspaceError(null)}
                    className="ml-auto text-red-500 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
              <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-amber-600" />
                  <div>
                    <span className="text-sm font-medium text-foreground">
                      Owned by {getWorkspaceName(templateDetails.workspace_id)}
                    </span>
                    <p className="text-xs text-muted-foreground">
                      This template belongs exclusively to this workspace and cannot be shared.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReleaseOwnership}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <RefreshCw className="h-3 w-3 animate-spin" />
                  ) : (
                    "Release ownership"
                  )}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div>
            {workspaceError && (
              <div className="mb-3 flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{workspaceError}</span>
                <button
                  onClick={() => setWorkspaceError(null)}
                  className="ml-auto text-red-500 hover:text-red-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            <KiiList<Workspace & { isAssigned: boolean }>
              title={
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-muted-foreground" />
                  <span>Workspace Access</span>
                </div>
              }
              description={t(isEditing ? "projectTemplates.detail.workspacesEditHint" : "projectTemplates.detail.workspacesViewHint")}
              filters={workspaceStatusFilters}
              filterValues={workspaceFilterValues}
              onFilterChange={setWorkspaceFilterValues}
              columns={workspaceColumns}
              data={workspacesWithStatus}
              showSearch
              searchPlaceholder="Filter workspaces..."
              searchQuery={workspaceSearch}
              onSearchQueryChange={setWorkspaceSearch}
              showCount
              countLabel={(count) => {
                const assignedCount = assignedWorkspaceIds.length;
                return `${assignedCount} assigned / ${count} workspace${count !== 1 ? "s" : ""}`;
              }}
              emptyMessage="No workspaces found"
              emptyIcon={<Building2 className="h-10 w-10 text-muted-foreground" />}
              renderRow={(ws) => (
                <TableRow key={ws.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div>
                        <span className="font-medium">{ws.name}</span>
                        {ws.handle && (
                          <span className="text-xs text-muted-foreground ml-2">@{ws.handle}</span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {ws.isAssigned ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-green-700 bg-green-50 dark:text-green-300 dark:bg-green-950 rounded-full">
                        <Check className="h-3 w-3" />
                        Assigned
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Not assigned</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {isEditing ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); toggleDraftWorkspace(ws.id); }}
                        className={ws.isAssigned ? "text-red-600 hover:text-red-800 border-red-300 hover:bg-red-50 dark:hover:bg-red-950" : undefined}
                      >
                        {ws.isAssigned ? "Remove" : "Add"}
                      </Button>
                    ) : ws.isAssigned && assignedWorkspaceIds.length === 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleMakeExclusive(ws.id); }}
                        disabled={isSaving}
                        className="text-amber-600 hover:text-amber-800 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950"
                      >
                        Make exclusive
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )}
            />
          </div>
        )}

        {/* Document Recipes */}
        <div className="bg-card rounded-lg border border-border">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-muted-foreground" />
                <h3 className="text-lg font-medium text-foreground">{t("projectTemplates.detail.documentRecipes")}</h3>
              </div>
              <p className="text-sm text-muted-foreground">{t("projectTemplates.detail.documentRecipesDescription")}</p>
            </div>
            <button
              onClick={handleOpenAssignDialog}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              {t("projectTemplates.detail.assignTemplate")}
            </button>
          </div>
          <div className="p-4">
            {(templateDetails.document_recipe_ids || []).length > 0 ? (
              <div className="space-y-2">
                {(templateDetails.document_recipe_ids || []).map((templateIdItem) => {
                  const shapeTemplate = availableShapeRecipes.find(t => t.id === templateIdItem);
                  // Unknown while the recipe list loads (or for a recipe missing from it): never assume "transcription",
                  // or an extraction recipe could be saved as the transcription default.
                  const templateScope = shapeTemplate?.input_mode;
                  const canSetDefault = templateScope === "transcription" || templateScope === "project";
                  const isDefaultTranscription = templateDetails.default_transcription_recipe_id === templateIdItem;
                  const isDefaultProject = templateDetails.default_project_recipe_id === templateIdItem;
                  const isDefault = isDefaultTranscription || isDefaultProject;

                  const handleToggleDefault = async () => {
                    try {
                      if (templateScope === "project") {
                        const newValue = isDefaultProject ? "" : templateIdItem;
                        await updateProjectTemplate(templateId!, {
                          defaultProjectRecipeId: newValue,
                        });
                      } else if (templateScope === "transcription") {
                        const newValue = isDefaultTranscription ? "" : templateIdItem;
                        await updateProjectTemplate(templateId!, {
                          defaultTranscriptionRecipeId: newValue,
                        });
                      }
                    } catch (err: any) {
                      console.error("Failed to toggle default template:", err);
                    }
                  };

                  return (
                    <div
                      key={templateIdItem}
                      data-testid={`template-recipe-${templateIdItem}`}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        isDefaultTranscription
                          ? "bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800"
                          : isDefaultProject
                            ? "bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800"
                            : "bg-muted"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className={`h-5 w-5 shrink-0 ${
                          isDefaultTranscription ? "text-blue-500" : isDefaultProject ? "text-purple-500" : "text-muted-foreground"
                        }`} />
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm text-foreground truncate">{shapeTemplate?.name || templateIdItem}</span>
                          {templateScope && (
                            <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${
                              templateScope === "project"
                                ? "text-purple-600 bg-purple-100 dark:bg-purple-900"
                                : "text-blue-600 bg-blue-100 dark:bg-blue-900"
                            }`}>
                              {templateScope}
                            </span>
                          )}
                          {isDefaultTranscription && (
                            <span className="text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900 px-2 py-0.5 rounded-full shrink-0">
                              Default transcription recipe
                            </span>
                          )}
                          {isDefaultProject && (
                            <span className="text-xs font-medium text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900 px-2 py-0.5 rounded-full shrink-0">
                              Default project recipe
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {canSetDefault && (
                        <button
                          onClick={handleToggleDefault}
                          className={`p-1 transition-colors ${
                            isDefault
                              ? isDefaultProject
                                ? "text-purple-600 hover:text-purple-800"
                                : "text-blue-600 hover:text-blue-800"
                              : "text-muted-foreground hover:text-muted-foreground"
                          }`}
                          title={isDefault ? "Remove as default" : `Set as default ${templateScope} recipe`}
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        )}
                        <button
                          onClick={() => handleRemoveDocumentRecipe(templateIdItem)}
                          className="p-1 text-muted-foreground hover:text-red-600 transition-colors"
                          title={t("projectTemplates.detail.removeTemplate")}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h4 className="font-medium text-foreground mb-2">{t("projectTemplates.detail.noDocumentRecipes")}</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  {t("projectTemplates.detail.noDocumentRecipesDescription")}
                </p>
                <button
                  onClick={handleOpenAssignDialog}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  {t("projectTemplates.detail.assignTemplate")}
                </button>
              </div>
            )}
          </div>
        </div>

        <ExtractionRecipePicker
          recipes={availableShapeRecipes}
          value={templateDetails.default_extraction_recipe_id}
          busy={isSavingExtraction || isLoadingShapeRecipes}
          failed={extractionSaveFailed}
          onChange={handleExtractionRecipeChange}
        />

        {/* Workspace Shared */}
        <div className="bg-card rounded-lg border border-border">
          <div className="px-4 py-3">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <h3 className="text-sm font-medium text-foreground">Workspace Shared</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Projects using this template are accessible across the entire workspace
                </p>
              </div>
              <input
                type="checkbox"
                checked={templateDetails.is_workspace_shared || false}
                onChange={async (e) => {
                  const newValue = e.target.checked;
                  try {
                    await updateProjectTemplate(templateId!, { isWorkspaceShared: newValue });
                  } catch (err) {
                    console.error("Failed to update workspace shared:", err);
                  }
                }}
                className="h-5 w-5 rounded border-input text-primary focus:ring-ring"
              />
            </label>
          </div>
        </div>

        {/* Raw Data (collapsible) */}
        <details className="text-sm">
          <summary className="text-muted-foreground cursor-pointer hover:text-foreground">
            {t("projectTemplates.detail.viewRawData")}
          </summary>
          <div className="mt-2 p-4 bg-muted rounded-lg font-mono text-xs">
            <pre className="whitespace-pre-wrap break-words">
              {JSON.stringify(templateDetails, null, 2)}
            </pre>
          </div>
        </details>
      </div>

      {/* Assign Document Recipe Dialog */}
      {showAssignDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-foreground mb-1">
                {t("projectTemplates.assignDialog.title")}
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                {t("projectTemplates.assignDialog.description")}
              </p>

              {isLoadingShapeRecipes ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-12 bg-muted rounded animate-pulse" />
                  ))}
                </div>
              ) : assignableShapeRecipes.length > 0 ? (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {assignableShapeRecipes.map((template) => (
                    <label
                      key={template.id}
                      className="flex items-center gap-3 p-3 border border-border rounded-lg hover:bg-accent cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={draftRecipeIds.includes(template.id)}
                        onChange={() => handleToggleDocumentRecipe(template.id)}
                        className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-foreground block truncate">
                          {template.name || template.id}
                        </span>
                        {template.description && (
                          <span className="text-sm text-muted-foreground block truncate">
                            {template.description}
                          </span>
                        )}
                      </div>
                      {draftRecipeIds.includes(template.id) && (
                        <Check className="h-4 w-4 text-blue-600 shrink-0" />
                      )}
                    </label>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">{t("projectTemplates.assignDialog.noTemplatesAvailable")}</p>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAssignDialog(false);
                  }}
                  disabled={isSaving}
                  className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
                >
                  {t("projectTemplates.assignDialog.cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleSaveDocumentRecipes}
                  disabled={isSaving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isSaving ? t("projectTemplates.assignDialog.saving") : t("projectTemplates.assignDialog.save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">
                    {t("projectTemplates.deleteDialog.title")}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {t("projectTemplates.deleteDialog.warning")}
                  </p>
                </div>
              </div>
              <p className="text-foreground mb-6">
                {t("projectTemplates.deleteDialog.confirm", { name: templateDetails.name })}
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteDialog(false)}
                  disabled={isDeleting}
                  className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
                >
                  {t("projectTemplates.deleteDialog.cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {isDeleting ? t("projectTemplates.deleteDialog.deleting") : t("projectTemplates.deleteDialog.delete")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
